import { Link, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { StateCard } from '../../components/StateCard';
import { PLATFORM_LABEL, SiteHead } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

interface DestinationPayload {
  target_id: string; label: string; platform: string; url: string;
  device_supported: boolean; leaves_app: boolean;
}

/**
 * P016 — interstitial before leaving for a store or an external game.
 * The destination is re-validated server-side on every view; this screen never
 * redirects on its own.
 */
export function PublicDestination() {
  const { targetId } = useParams();
  const [searchParams] = useSearchParams();
  const platform = searchParams.get('platform') ?? '';

  const destination = useResource<DestinationPayload>(
    (signal) => api.public(`/api/v1/destinations/${targetId}`, {
      signal,
      query: { platform, promotion_id: searchParams.get('promotion_id') ?? undefined },
    }) as Promise<ApiEnvelope<DestinationPayload>>,
    [targetId, platform],
    { emptyWhen: () => false },
  );

  const data = destination.data;
  let host = '';
  if (data) {
    try {
      host = new URL(data.url).host;
    } catch {
      host = data.url;
    }
  }

  return (
    <>
      <SiteHead
        eyebrow="이동 확인"
        title="ASKRS Games를 벗어납니다"
        lead="주소 확인 후 이동 버튼 선택"
      />

      <StateCard state={destination.state} error={destination.error} onRetry={destination.reload}>
        {data && (
          <div className="card">
            <dl className="spec-list">
              <dt>대상</dt><dd>{data.label}</dd>
              <dt>플랫폼</dt><dd>{PLATFORM_LABEL[data.platform] ?? data.platform}</dd>
              <dt>주소</dt><dd style={{ wordBreak: 'break-all' }}>{host}</dd>
            </dl>

            {!data.device_supported && (
              <div className="banner" style={{ marginTop: 20 }}>
                <span aria-hidden="true">!</span>
                <div>현재 기기 미지원 · 설치 가능 여부 확인 필요</div>
              </div>
            )}

            <div className="banner banner--info" style={{ marginTop: 20 }}>
              <span aria-hidden="true">ℹ</span>
              <div>외부 사이트의 이용약관·개인정보 처리방침 적용</div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 24, flexWrap: 'wrap' }}>
              <a className="btn btn--primary" href={data.url} target="_blank" rel="noopener noreferrer external">
                {host}(으)로 이동
              </a>
              <Link className="btn" to="/games">돌아가기</Link>
            </div>
          </div>
        )}
      </StateCard>
    </>
  );
}
